import PropTypes from 'prop-types';
import React from 'react';

import logoLargeBundled from '@sophocles/public/odl_logo.png';
import logoSmallBundled from '@sophocles/public/odl_logo_small.png';

import { useTenantTheme } from './TenantThemeProvider';

// Tenant-aware brand logo.  Use this anywhere the ODL logo renders so
// a white-labeled tenant picks up its own brand assets.
//
// Resolution priority (first truthy value wins):
//
//   size="large", !inverse  →  logo.large           → bundled odl_logo.png
//   size="large",  inverse  →  logo.largeInverse     → logo.large → bundled odl_logo.png
//   size="small", !inverse  →  logo.small            → logo.large → bundled odl_logo_small.png
//   size="small",  inverse  →  logo.smallInverse     → logo.small → logo.large → bundled odl_logo_small.png
//
// Props:
//   size    — 'large' (full wordmark) or 'small' (square mark / favicon).
//             Defaults to 'large'.
//   inverse — request the light/inverse tone for use on dark backgrounds
//             (e.g. teal nav).  Defaults to false.
//   alt     — accessible label; defaults to the tenant brand name.
const BrandLogo = ({ size = 'large', inverse = false, alt, ...rest }) => {
  const { brand, logo } = useTenantTheme();

  let src;
  if (size === 'small') {
    if (inverse) {
      src = logo?.smallInverse || logo?.small || logo?.large || logoSmallBundled;
    } else {
      src = logo?.small || logo?.large || logoSmallBundled;
    }
  } else {
    if (inverse) {
      src = logo?.largeInverse || logo?.large || logoLargeBundled;
    } else {
      src = logo?.large || logoLargeBundled;
    }
  }

  if (!src) return null;
  return <img src={src} alt={alt || brand?.name || 'Brand logo'} {...rest} />;
};

BrandLogo.propTypes = {
  size: PropTypes.oneOf(['large', 'small']),
  inverse: PropTypes.bool,
  alt: PropTypes.string,
};

export default BrandLogo;
